/**
 * AOTI — Auto-Optimización Inteligente de Tamaño de Imagen
 * MÓDULO UNIFICADO — Implementación literal del Cuadro Negro
 *
 * Sistema global obligatorio para TODAS las herramientas del Área de Trabajo
 * que procesan imágenes mediante IA o manipulación interna.
 *
 * Herramientas cubiertas: Outpainting, Background Removal,
 * Variantes IA, Filtros IA, Upscale y cualquier herramienta futura.
 *
 * Reglas:
 * - Se ejecuta SIEMPRE que el usuario cargue una imagen
 * - La optimización SOLO se aplica en el momento del doble click (si aceptó)
 * - La IA SIEMPRE trabaja con IMAGE_OPTIMIZED (si aceptó) o la original (si rechazó)
 * - Redimensionar en el canvas NO afecta el tamaño interno usado por la IA
 */

/**
 * Tamaños óptimos obligatorios por herramienta IA (lado mayor en píxeles).
 * Estos valores NO pueden ser modificados.
 */
const TOOL_OPTIMAL_SIZES = {
  background_removal: 2048,
  outpainting:        2048,
  variants:           1536,
  filters:            1536,
  upscale:            1024
};

/**
 * Tamaño máximo general para IMAGE_OPTIMIZED (el mayor de todos los óptimos).
 * Se usa en el doble click cuando aún no hay herramienta activa.
 */
const AOTI_GENERAL_MAX = 2048;

/**
 * Analizar si una imagen necesita optimización AOTI.
 * Se ejecuta al agregar la imagen al Área de Trabajo.
 *
 * @param {number} width  - Ancho original
 * @param {number} height - Alto original
 * @returns {{ needsOptimization: boolean, optimalWidth: number, optimalHeight: number, originalWidth: number, originalHeight: number }}
 */
export function analyzeImage(width, height) {
  const longestSide = Math.max(width, height);

  if (longestSide <= AOTI_GENERAL_MAX) {
    return {
      needsOptimization: false,
      optimalWidth: width,
      optimalHeight: height,
      originalWidth: width,
      originalHeight: height
    };
  }

  const scale = AOTI_GENERAL_MAX / longestSide;
  const optimalWidth = Math.round(width * scale);
  const optimalHeight = Math.round(height * scale);

  return {
    needsOptimization: true,
    optimalWidth,
    optimalHeight,
    originalWidth: width,
    originalHeight: height
  };
}

/**
 * Optimizar una imagen al tamaño óptimo general (IMAGE_OPTIMIZED).
 * Se invoca en el momento del doble click si el usuario aceptó.
 * La imagen se ajusta al tamaño perfecto (max 2048px lado mayor).
 *
 * @param {HTMLImageElement} imgElement
 * @returns {Promise<{ dataUrl: string, width: number, height: number }>}
 */
export function optimizeImage(imgElement) {
  return new Promise((resolve, reject) => {
    try {
      const srcW = imgElement.naturalWidth || imgElement.width;
      const srcH = imgElement.naturalHeight || imgElement.height;
      const analysis = analyzeImage(srcW, srcH);

      const canvas = document.createElement('canvas');
      canvas.width = analysis.optimalWidth;
      canvas.height = analysis.optimalHeight;

      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(imgElement, 0, 0, analysis.optimalWidth, analysis.optimalHeight);

      resolve({
        dataUrl: canvas.toDataURL('image/png'),
        width: analysis.optimalWidth,
        height: analysis.optimalHeight
      });
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Obtener un canvas a dimensiones AOTI para procesamiento IA interno.
 *
 * Flujo:
 * 1. Si hay IMAGE_OPTIMIZED (aotiDimensions), escala al tamaño base (≤2048).
 * 2. Luego ajusta al tamaño óptimo de la herramienta específica.
 *
 * @param {HTMLCanvasElement} canvas         - Canvas actual del editor
 * @param {{ width: number, height: number }|null} aotiDimensions - Dimensiones IMAGE_OPTIMIZED
 * @param {string} [toolType='background_removal'] - Tipo de herramienta activa
 * @returns {HTMLCanvasElement}
 */
export function getAOTIProcessingCanvas(canvas, aotiDimensions, toolType) {
  // Paso 1: Obtener canvas base a dimensiones IMAGE_OPTIMIZED
  let baseCanvas = canvas;
  if (aotiDimensions && (canvas.width !== aotiDimensions.width || canvas.height !== aotiDimensions.height)) {
    baseCanvas = document.createElement('canvas');
    baseCanvas.width = aotiDimensions.width;
    baseCanvas.height = aotiDimensions.height;
    const ctx = baseCanvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(canvas, 0, 0, aotiDimensions.width, aotiDimensions.height);
  }

  // Paso 2: Ajustar al tamaño óptimo de la herramienta específica
  const toolMax = TOOL_OPTIMAL_SIZES[toolType] || AOTI_GENERAL_MAX;
  const longestSide = Math.max(baseCanvas.width, baseCanvas.height);

  if (longestSide <= toolMax) {
    return baseCanvas;
  }

  const scale = toolMax / longestSide;
  const targetW = Math.round(baseCanvas.width * scale);
  const targetH = Math.round(baseCanvas.height * scale);

  const toolCanvas = document.createElement('canvas');
  toolCanvas.width = targetW;
  toolCanvas.height = targetH;

  const tCtx = toolCanvas.getContext('2d');
  tCtx.imageSmoothingEnabled = true;
  tCtx.imageSmoothingQuality = 'high';
  tCtx.drawImage(baseCanvas, 0, 0, targetW, targetH);

  return toolCanvas;
}
